/**
 * /ko/comparisons/obsidian — Draftlize vs Obsidian (Korean). 옵시디언 9.8k —
 * highest-volume KO comparison. Angle: Obsidian = personal note vault;
 * Draftlize = team decision substrate for AI agents. Links to /ko/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function ObsidianKo() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/comparisons/obsidian"
      hreflangLocales={['ko']}
      documentTitle="옵시디언으로 제품 관리, 한계는?｜Draftlize와 비교 · Draftlize"
      documentDescription="옵시디언은 개인 노트에 강합니다. 팀의 '의사결정'을 살아있는 상태로 공유하려면? Draftlize와 옵시디언의 차이를 정리합니다."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">비교 · vs 옵시디언</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            옵시디언 vs Draftlize——<br />
            <em>개인 노트와 팀의 결정.</em>
          </h1>
          <p className="lede">
            옵시디언은 로컬 마크다운으로 개인 지식을 쌓는 최고의 노트 금고입니다. 파일을 소유하고, 오프라인으로 쓰고, 그래프로 연결합니다. Draftlize는 다른 문제를 풉니다——팀의 <em>의사결정</em>을 AI 에이전트가 읽고 쓸 수 있는 구조화된 카드로 살아있게 유지합니다. 옵시디언이 「개인의 뇌」라면, Draftlize는 「팀의 결정 기반」입니다.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">요점</span>
            <h2 className="serif">나란히 비교, <em>7가지 관점.</em></h2>
          </div>
          <p>개인 지식 정리인가, 팀 결정 관리인가——문제부터 다릅니다.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>옵시디언</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>기반의 형태</td><td className="ours yes">타입이 있는 결정 카드 그래프</td><td className="partial">로컬 마크다운 금고</td></tr>
            <tr><td>로컬 우선・파일 소유・오프라인</td><td className="no">클라우드</td><td className="yes">탁월</td></tr>
            <tr><td>개인 지식・백링크・그래프 뷰</td><td className="partial">결정 중심</td><td className="yes">탁월</td></tr>
            <tr><td>플러그인 생태계</td><td className="no">—</td><td className="yes">방대</td></tr>
            <tr><td>AI가 결정을 컨텍스트로 읽음</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
            <tr><td>의존이 바뀌면 stale 전파</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
            <tr><td>Claude Code / MCP 연동</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>옵시디언이 이기는</em> 지점.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">파일을 소유하고 싶다면</h3>
            <p>옵시디언은 로컬 마크다운입니다. 데이터가 내 디스크에 있고, 오프라인으로 쓰고, 어떤 서비스에도 묶이지 않습니다. 이게 중요하다면 옵시디언.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">개인 지식(PKM)이라면</h3>
            <p>백링크, 그래프 뷰, 데일리 노트——옵시디언의 개인 지식 관리는 최고 수준입니다. Draftlize는 개인 노트 앱을 지향하지 않습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">플러그인으로 커스터마이즈</h3>
            <p>수백 개의 커뮤니티 플러그인으로 원하는 대로 확장됩니다. Draftlize는 그런 확장성을 지향하지 않습니다.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize가 이기는</em> 지점.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">팀의 결정을 공유한다</h3>
            <p>옵시디언은 기본적으로 1인용 노트입니다. Draftlize는 팀의 결정을 참조 가능한 카드로 공유합니다——누가 언제 왜 정했는지, 무엇이 그것에 의존하는지.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">결정이 낡으면 알려준다</h3>
            <p>옵시디언 노트를 바꿔도 아무도 모릅니다. Draftlize의 결정 카드를 바꾸면 의존하는 카드가 자동으로 <code>stale</code>. 빌드 시스템이 무효화를 추적하듯.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 에이전트가 읽고 쓴다</h3>
            <p>옵시디언은 사람이 읽는 금고입니다. Draftlize 카드는 MCP로 Claude Code·Cursor가 읽고 씁니다. 이어서 <a href="/ko/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>의사결정 기록</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          옵시디언은 "내 지식을 어디에 쌓는가"의 답. Draftlize는 "팀의 결정을 어떻게 살아있게 유지하는가"의 답.
        </blockquote>
        <cite>개인 지식이 목적이라면, 옵시디언으로 충분합니다.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">$5 무료로 <em>시작.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            옵시디언은 개인 노트로 계속 쓰세요. 팀의 결정은 Draftlize에서 살아있는 카드로. 신규 가입 시 $5, 사용한 만큼만, 잔액은 만료되지 않음.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
